import { CAMERA_CONFIG } from './cameraConfig';

/**
 * Camera animation utilities
 */

/**
 * Animate camera to target position with smooth easing
 * @param {Object} component - Component instance
 * @param {Object} targetCamera - Target camera state {x, y, scale}
 */
export const animateToCamera = (component, targetCamera) => {
  const startCamera = { ...component.state.camera };
  const duration = CAMERA_CONFIG.ANIMATION_DURATION;
  const startTime = Date.now();

  if (component.cameraAnimation) {
    cancelAnimationFrame(component.cameraAnimation);
  }

  // Use transform: scale() during animation for smooth performance
  component.setState({ isActivelyZooming: true });

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    const easeInOut = progress => {
      return progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;
    };

    const easedProgress = easeInOut(progress);

    const currentCamera = {
      x: startCamera.x + (targetCamera.x - startCamera.x) * easedProgress,
      y: startCamera.y + (targetCamera.y - startCamera.y) * easedProgress,
      scale: startCamera.scale + (targetCamera.scale - startCamera.scale) * easedProgress
    };

    component.setState({ camera: currentCamera });

    if (progress < 1) {
      component.cameraAnimation = requestAnimationFrame(animate);
    } else {
      component.cameraAnimation = null;
      // Animation complete - switch to CSS zoom for crisp text after short delay
      if (component.zoomIdleTimeout) {
        clearTimeout(component.zoomIdleTimeout);
      }
      component.zoomIdleTimeout = setTimeout(() => {
        component.setState({ isActivelyZooming: false });
      }, 50);
    }
  };

  animate();
};

/**
 * Focus camera on a specific node
 * @param {Object} component - Component instance
 * @param {string} nodeId - Node ID to focus on
 * @param {number} zoomStep - 1-indexed zoom step (see CAMERA_CONFIG.ZOOM_STEP_MULTIPLIERS);
 *   re-clicking an already-focused card advances this to zoom in closer.
 */
export const focusOnNode = (component, nodeId, zoomStep = 1) => {
  const { nodePositions } = component.state;
  const { allNodes } = component.props;

  if (!nodePositions.has(nodeId) || !allNodes) return;

  const container = component.containerRef.current;
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const centerX = containerRect.width / 2;

  const targetNode = allNodes.find(n => n.id === nodeId);
  if (!targetNode) return;

  const targetPos = nodePositions.get(nodeId);
  const children = allNodes.filter(n => n.parentId === nodeId);

  // Mobile detection (works in both portrait and landscape)
  const isMobile = typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) <= 768;
  const isLandscape = typeof window !== 'undefined' && window.innerHeight < window.innerWidth;
  const isMobileLandscape = isMobile && isLandscape;

  // Use configuration for zoom and vertical alignment
  let maxZoom = children.length === 0
    ? CAMERA_CONFIG.MAX_ZOOM_LEAF
    : CAMERA_CONFIG.MAX_ZOOM_WITH_CHILDREN;
  
  if (isMobile) {
    // Landscape: reduce zoom (0.5x) for better overview with limited vertical space
    // Portrait: increase zoom (1.5x) for better card visibility
    const zoomMultiplier = isMobileLandscape ? 0.5 : 0.9;
    maxZoom = Math.min(maxZoom * zoomMultiplier, 0.9);
  } else {
    // Desktop: reduce zoom a bit more for better overview (0.75x)
    maxZoom = maxZoom * 0.75;
  }
  
  const stepMultipliers = CAMERA_CONFIG.ZOOM_STEP_MULTIPLIERS || [1];
  const clampedStep = Math.max(1, Math.min(zoomStep, stepMultipliers.length));
  const finalScale = Math.max(maxZoom, CAMERA_CONFIG.MIN_ZOOM) * stepMultipliers[clampedStep - 1];

  // Use configuration for vertical alignment
  const verticalDivisor = children.length === 0
    ? CAMERA_CONFIG.FOCUS_Y_WITHOUT_CHILDREN
    : CAMERA_CONFIG.FOCUS_Y_WITH_CHILDREN;
  const targetY = containerRect.height / verticalDivisor;

  animateToCamera(component, {
    x: centerX - (targetPos.x * finalScale),
    y: targetY - (targetPos.y * finalScale),
    scale: finalScale
  });

  component.setState({ selectedNodeId: nodeId, zoomStep: clampedStep });
};

/**
 * Reset camera to show overview of entire tree
 * @param {Object} component - Component instance
 */
export const resetCamera = (component) => {
  const container = component.containerRef.current;
  const { nodePositions } = component.state;

  if (!container) {
    component.setState({
      camera: { x: 0, y: 0, scale: 0.18 }
    });
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const centerX = containerRect.width / 2;
  const centerY = containerRect.height / 2;

  if (!nodePositions || nodePositions.size === 0) {
    component.setState({
      camera: {
        x: centerX - (CAMERA_CONFIG.SUBTREE_PADDING + 800) * 0.18,
        y: centerY - 400 * 0.18,
        scale: 0.18
      }
    });
    return;
  }

  const positions = Array.from(nodePositions.values());
  const minX = Math.min(...positions.map(pos => pos.x));
  const maxX = Math.max(...positions.map(pos => pos.x));
  const minY = Math.min(...positions.map(pos => pos.y));
  const maxY = Math.max(...positions.map(pos => pos.y));

  const worldWidth = maxX - minX + 800;
  const worldHeight = maxY - minY + 600;

  const scaleX = containerRect.width / worldWidth;
  const scaleY = containerRect.height / worldHeight;
  const scale = Math.min(scaleX, scaleY, 0.5) * 0.85;

  const worldCenterX = (minX + maxX) / 2;
  const worldCenterY = (minY + maxY) / 2;

  component.setState({
    camera: {
      x: centerX - (worldCenterX * scale),
      y: centerY - (worldCenterY * scale),
      scale: scale
    }
  });
};

/**
 * Center camera on currently selected node
 * @param {Object} component - Component instance
 */
export const centerCameraOnSelectedNode = (component) => {
  const { selectedNodeId, nodePositions } = component.state;

  if (!selectedNodeId || !nodePositions.has(selectedNodeId)) return;

  const nodePos = nodePositions.get(selectedNodeId);
  const container = component.containerRef.current;

  if (container && nodePos) {
    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;

    component.setState(prevState => ({
      camera: {
        ...prevState.camera,
        x: centerX - (nodePos.x * prevState.camera.scale),
        y: centerY - (nodePos.y * prevState.camera.scale)
      }
    }));
  }
};

/**
 * Update camera position based on keyboard input
 * @param {Object} component - Component instance
 */
export const updateCameraFromKeyboard = (component) => {
  const { camera } = component.state;
  const speed = 5 / camera.scale;

  let deltaX = 0;
  let deltaY = 0;

  if (component.keys.has('arrowup')) deltaY += speed;
  if (component.keys.has('arrowdown')) deltaY -= speed;
  if (component.keys.has('arrowleft')) deltaX += speed;
  if (component.keys.has('arrowright')) deltaX -= speed;

  if (deltaX !== 0 || deltaY !== 0) {
    component.setState(prevState => ({
      camera: {
        ...prevState.camera,
        x: prevState.camera.x + deltaX,
        y: prevState.camera.y + deltaY
      }
    }));
  }
};
